import type { CoachProfile } from '@efdigital/ll-addon-data';

export type CoachProfileWithRelevance = {
  coachProfile: CoachProfile;
  relevance: number;
};

export type Sort = 'combinedScore' | 'experience';

export type Tab = 'pool' | 'shortlist' | 'selected';

export const SORT_OPTIONS: { value: Sort; label: string }[] = [
  { value: 'combinedScore', label: 'Best match' },
  { value: 'experience', label: 'Experience' },
];

export interface CoachProfileSearch {
  searchQuery: string;
  specialty: string | null;
  language: string | null;
  industry: string | null;
  country: string | null;
}

export type MatchedCoachProfile = CoachProfileWithRelevance & {
  match: boolean;
  confidence: number;
  combinedScore: number;
};

export function isValidSort(value: string): value is Sort {
  return SORT_OPTIONS.some((option) => option.value === value);
}

export function validatedSort(value: string): Sort {
  return isValidSort(value) ? value : 'combinedScore';
}

export function matchesSearch(
  coach: CoachProfileWithRelevance,
  search: CoachProfileSearch
): { match: boolean; confidence: number } {
  let confidence = 0;
  let match = true;
  if (search.searchQuery) {
    const text = [
      coach.coachProfile.prefix,
      coach.coachProfile.firstName,
      coach.coachProfile.lastName,
      coach.coachProfile.suffix,
      coach.coachProfile.bio,
      coach.coachProfile.blurb,
      coach.coachProfile.approachToCoaching,
    ]
      .filter((i) => i)
      .join(' ');
    if (text.toLowerCase().includes(search.searchQuery.toLowerCase())) {
      confidence += 1;
    } else {
      match = false;
    }
  }
  if (search.specialty) {
    if (coach.coachProfile.specialties.includes(search.specialty)) {
      confidence += 1;
    } else {
      match = false;
    }
  }
  if (search.language) {
    if (
      coach.coachProfile.languages.find(
        (language) => language.language === search.language
      )
    ) {
      confidence += 1;
    } else {
      match = false;
    }
  }
  if (search.industry) {
    if (coach.coachProfile.industries.includes(search.industry)) {
      confidence += 1;
    } else {
      match = false;
    }
  }
  if (search.country) {
    if (coach.coachProfile.countries.includes(search.country)) {
      confidence += 1;
    } else {
      match = false;
    }
  }
  return { match, confidence };
}

function sortFunction(
  sort: Sort
): (a: MatchedCoachProfile, b: MatchedCoachProfile) => number {
  switch (sort) {
    case 'combinedScore':
      return (a, b) => b.combinedScore - a.combinedScore;
    case 'experience':
      return (a, b) =>
        (b.coachProfile.yearsCoached ?? 0) - (a.coachProfile.yearsCoached ?? 0);
  }
}

export function hasActiveSearch(search: CoachProfileSearch): boolean {
  if (
    search.searchQuery?.trim() ||
    search.specialty ||
    search.language ||
    search.industry ||
    search.country
  ) {
    return true;
  }
  return false;
}

export function filterCoachProfiles(
  pool: CoachProfileWithRelevance[],
  search: CoachProfileSearch,
  sort: Sort
): MatchedCoachProfile[] {
  const sorter = sortFunction(sort);
  const needsFiltering = hasActiveSearch(search);

  return pool
    .map((coach) => {
      const match = needsFiltering
        ? matchesSearch(coach, search)
        : { match: true, confidence: 1 };
      return {
        ...coach,
        ...match,
        combinedScore: match.confidence + coach.relevance,
      };
    })
    .filter((coach) => coach.match)
    .sort(sorter);
}
